import { useEffect, useState, useCallback } from 'react';
import { Typography, Box } from '@mui/material';

import { useApi } from '@/providers/ApiContext';
import { getPlotElements } from '@/actions/plotElements';
import PlotElement from './PlotElement';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import type {
  PlotSection as PlotSectionType,
  PlotElement as PlotElementType,
  EntityId,
} from '@/types';

interface PlotSectionProps {
  section: PlotSectionType;
  plotId: string | undefined;
  refreshTrigger: number;
  plotStatus?: string;
}

function PlotSection({
  section,
  plotId,
  refreshTrigger,
  plotStatus,
}: PlotSectionProps): React.JSX.Element {
  const api = useApi();
  const [elements, setElements] = useState<PlotElementType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const fetchElements = useCallback(async () => {
    if (!plotId || !section.entity_id || !api) return;

    try {
      setLoading(true);
      setApiError(null);
      const data = await getPlotElements(api, plotId as EntityId, section.entity_id);
      setElements(data.results || []);
    } catch (err) {
      console.error('Error fetching plot elements:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Unable to load plot elements. This could be due to network issues, server problems, or the section may not exist.';
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [api, plotId, section.entity_id]);

  useEffect(() => {
    fetchElements();
  }, [fetchElements]);

  // Refresh elements when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger) {
      fetchElements();
    }
  }, [refreshTrigger, fetchElements]);

  // Poll for element updates when any element is queued or running
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const hasActiveElements = elements.some(
      (element) => element.status === 'queued' || element.status === 'running',
    );

    if (hasActiveElements) {
      interval = setInterval(fetchElements, 5000); // Poll every 5 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [elements, fetchElements]);

  const getHeadingVariant = (style: string): 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' => {
    switch (style) {
      case 'h1':
        return 'h1';
      case 'h2':
        return 'h2';
      case 'h3':
        return 'h3';
      case 'h4':
        return 'h4';
      case 'h5':
        return 'h5';
      case 'h6':
        return 'h6';
      default:
        return 'h3';
    }
  };

  const getHeadingSx = (style: string) => {
    const baseSx = { mb: 2, mt: 3 };

    switch (style) {
      case 'h1':
        return { ...baseSx, fontSize: '2rem', fontWeight: 'bold' };
      case 'h2':
        return { ...baseSx, fontSize: '1.5rem', fontWeight: 'bold' };
      case 'h3':
        return { ...baseSx, fontSize: '1.25rem', fontWeight: 'bold' };
      case 'h4':
        return { ...baseSx, fontSize: '1.1rem', fontWeight: 'bold' };
      case 'h5':
        return { ...baseSx, fontSize: '1rem', fontWeight: 'bold' };
      case 'h6':
        return { ...baseSx, fontSize: '0.9rem', fontWeight: 'bold' };
      default:
        return { ...baseSx, fontSize: '1.25rem', fontWeight: 'bold' };
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant={getHeadingVariant(section.style)}
        sx={getHeadingSx(section.style)}
      >
        {section.number && `${section.number}. `}
        {section.heading}
      </Typography>

      {loading ? (
        <Typography variant='body2' color='text.secondary'>
          Loading plot elements...
        </Typography>
      ) : elements.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No plot elements found for this section.
        </Typography>
      ) : (
        elements.map((element, index) => (
          <PlotElement
            key={element.entity_id || index}
            element={element}
            plotId={plotId}
            sectionId={section.entity_id}
            plotStatus={plotStatus}
            onElementUpdated={fetchElements}
          />
        ))
      )}

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Box>
  );
}

export default PlotSection;

